import React, { useState, useEffect } from 'react';
import api from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Shield, Key, Copy, Plus, Trash2, Loader2, RefreshCw, Users, Lock, Globe } from 'lucide-react';

interface SSOProvider {
  id: string;
  display_name: string;
  type: 'saml' | 'oidc';
  enabled: boolean;
  last_login_at: string | null;
  entity_id?: string;
  sso_url?: string;
  certificate?: string;
  client_id?: string;
  client_secret?: string;
  discovery_url?: string;
  auto_discovery?: boolean;
  created_at: string;
}

interface SCIMConfig {
  enabled: boolean;
  endpoint_url: string;
  token_hint: string | null;
  last_sync_at: string | null;
  synced_users_count: number;
  synced_groups_count: number;
}

interface NewProviderForm {
  type: 'saml' | 'oidc';
  display_name: string;
  entity_id: string;
  sso_url: string;
  certificate: string;
  client_id: string;
  client_secret: string;
  discovery_url: string;
  auto_discovery: boolean;
}

const initialProviderForm: NewProviderForm = {
  type: 'saml',
  display_name: '',
  entity_id: '',
  sso_url: '',
  certificate: '',
  client_id: '',
  client_secret: '',
  discovery_url: '',
  auto_discovery: false,
};

export function SSOAdminPage() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('sso');
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [scimConfig, setScimConfig] = useState<SCIMConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [scimLoading, setScimLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<SSOProvider | null>(null);
  const [form, setForm] = useState<NewProviderForm>(initialProviderForm);
  const [submitting, setSubmitting] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [newScimToken, setNewScimToken] = useState<string | null>(null);

  useEffect(() => {
    fetchProviders();
    fetchScimConfig();
  }, []);

  async function fetchProviders() {
    setLoading(true);
    try {
      const res = await api.get('/sso/admin/providers');
      setProviders(res.data ?? []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load SSO providers.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }

  async function fetchScimConfig() {
    setScimLoading(true);
    try {
      const res = await api.get('/scim/config');
      setScimConfig(res.data);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        setScimConfig(null);
      } else {
        toast({ title: 'Error', description: 'Failed to load SCIM configuration.', variant: 'destructive' });
      }
    } finally {
      setScimLoading(false);
    }
  }

  async function handleCreateProvider() {
    if (!form.display_name.trim()) {
      toast({ title: 'Validation', description: 'Display name is required.', variant: 'destructive' });
      return;
    }

    if (form.type === 'saml') {
      if (!form.entity_id.trim() || !form.sso_url.trim() || !form.certificate.trim()) {
        toast({ title: 'Validation', description: 'Entity ID, SSO URL, and Certificate are required for SAML.', variant: 'destructive' });
        return;
      }
    } else {
      if (!form.client_id.trim() || !form.discovery_url.trim()) {
        toast({ title: 'Validation', description: 'Client ID and Discovery URL are required for OIDC.', variant: 'destructive' });
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        type: form.type,
        display_name: form.display_name.trim(),
      };

      if (form.type === 'saml') {
        payload.entity_id = form.entity_id.trim();
        payload.sso_url = form.sso_url.trim();
        payload.certificate = form.certificate.trim();
      } else {
        payload.client_id = form.client_id.trim();
        payload.client_secret = form.client_secret.trim();
        payload.discovery_url = form.discovery_url.trim();
        payload.auto_discovery = form.auto_discovery;
      }

      await api.post('/sso/admin/providers', payload);
      toast({ title: 'Provider created', description: `${form.display_name} has been configured.` });
      setAddDialogOpen(false);
      setForm(initialProviderForm);
      await fetchProviders();
    } catch {
      toast({ title: 'Error', description: 'Failed to create SSO provider.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteProvider() {
    if (!providerToDelete) return;
    setSubmitting(true);
    try {
      await api.delete('/sso/admin/providers/' + providerToDelete.id);
      toast({ title: 'Provider deleted', description: `${providerToDelete.display_name} has been removed.` });
      setDeleteDialogOpen(false);
      setProviderToDelete(null);
      await fetchProviders();
    } catch {
      toast({ title: 'Error', description: 'Failed to delete provider.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  }

  function openDeleteDialog(provider: SSOProvider) {
    setProviderToDelete(provider);
    setDeleteDialogOpen(true);
  }

  async function handleToggleScim(enabled: boolean) {
    try {
      const res = await api.put('/scim/config', { enabled });
      setScimConfig(res.data);
      toast({ title: enabled ? 'SCIM enabled' : 'SCIM disabled' });
    } catch {
      toast({ title: 'Error', description: 'Failed to update SCIM configuration.', variant: 'destructive' });
    }
  }

  async function handleGenerateScimToken() {
    setGeneratingToken(true);
    try {
      const res = await api.put('/scim/config', { regenerate_token: true });
      if (res.data?.plaintext_token) {
        setNewScimToken(res.data.plaintext_token);
      }
      setScimConfig(res.data?.config ?? res.data);
      toast({ title: 'SCIM token generated', description: 'Copy the token now. It will not be shown again.' });
    } catch {
      toast({ title: 'Error', description: 'Failed to generate SCIM token.', variant: 'destructive' });
    } finally {
      setGeneratingToken(false);
    }
  }

  async function handleRevokeScimToken() {
    try {
      const res = await api.put('/scim/config', { revoke_token: true });
      setScimConfig(res.data);
      setNewScimToken(null);
      toast({ title: 'SCIM token revoked' });
    } catch {
      toast({ title: 'Error', description: 'Failed to revoke SCIM token.', variant: 'destructive' });
    }
  }

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied', description: `${label} copied to clipboard.` });
  }

  function formatDateTime(dateStr: string | null) {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  }

  return (
    <div className="container mx-auto max-w-5xl py-8 px-4">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Shield className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold tracking-tight">SSO &amp; Directory Sync</h1>
        </div>
        <p className="text-muted-foreground">
          Configure enterprise single sign-on providers and SCIM directory synchronization.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="sso" className="gap-2">
            <Lock className="h-4 w-4" />
            SSO Providers
          </TabsTrigger>
          <TabsTrigger value="scim" className="gap-2">
            <Users className="h-4 w-4" />
            SCIM Directory Sync
          </TabsTrigger>
        </TabsList>

        {/* SSO Providers Tab */}
        <TabsContent value="sso">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold">Identity Providers</h2>
              <p className="text-sm text-muted-foreground">
                Connect SAML 2.0 or OpenID Connect providers for single sign-on.
              </p>
            </div>
            <Button onClick={() => { setForm(initialProviderForm); setAddDialogOpen(true); }} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Provider
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : providers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <Shield className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">No SSO providers configured</h3>
                <p className="text-sm text-muted-foreground mb-6 max-w-md">
                  Add a SAML 2.0 or OIDC provider to enable single sign-on for your organization.
                </p>
                <Button onClick={() => { setForm(initialProviderForm); setAddDialogOpen(true); }} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Provider
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {providers.map((provider) => (
                <Card key={provider.id}>
                  <CardContent className="flex items-center justify-between p-6">
                    <div className="flex items-center gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        {provider.type === 'saml' ? (
                          <Lock className="h-5 w-5 text-primary" />
                        ) : (
                          <Globe className="h-5 w-5 text-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold">{provider.display_name}</h3>
                          <Badge variant="outline" className="uppercase text-xs">
                            {provider.type}
                          </Badge>
                          <Badge variant={provider.enabled ? 'default' : 'secondary'}>
                            {provider.enabled ? 'Enabled' : 'Disabled'}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          Last login: {formatDateTime(provider.last_login_at)}
                          {provider.created_at && (
                            <span className="ml-4">Created: {formatDateTime(provider.created_at)}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => openDeleteDialog(provider)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* SCIM Tab */}
        <TabsContent value="scim">
          <div className="mb-6">
            <h2 className="text-xl font-semibold">SCIM Directory Synchronization</h2>
            <p className="text-sm text-muted-foreground">
              Automatically provision and deprovision users and groups from your identity provider.
            </p>
          </div>

          {scimLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enable/Disable */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-base">Enable SCIM</CardTitle>
                      <CardDescription>
                        Allow your identity provider to manage users and groups via SCIM 2.0.
                      </CardDescription>
                    </div>
                    <Switch
                      checked={scimConfig?.enabled ?? false}
                      onCheckedChange={handleToggleScim}
                    />
                  </div>
                </CardHeader>
              </Card>

              {/* SCIM Endpoint */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">SCIM Endpoint</CardTitle>
                  <CardDescription>
                    Configure your identity provider with this endpoint URL.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-2">
                    <Input
                      readOnly
                      value={scimConfig?.endpoint_url ?? `${window.location.origin}/api/scim/v2`}
                      className="font-mono text-sm bg-muted"
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => copyToClipboard(
                        scimConfig?.endpoint_url ?? `${window.location.origin}/api/scim/v2`,
                        'SCIM endpoint URL'
                      )}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Token Management */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Bearer Token</CardTitle>
                  <CardDescription>
                    Generate a token for your identity provider to authenticate SCIM requests.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {newScimToken && (
                    <Alert className="border-amber-500/50 bg-amber-500/10">
                      <Key className="h-4 w-4 text-amber-600" />
                      <AlertTitle className="text-amber-600">Copy your SCIM token now</AlertTitle>
                      <AlertDescription>
                        <p className="text-sm text-amber-600 mb-3">
                          This token will only be shown once. Store it securely.
                        </p>
                        <div className="flex items-center gap-2">
                          <Input
                            readOnly
                            value={newScimToken}
                            className="font-mono text-sm bg-amber-50 dark:bg-amber-950 border-amber-300"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => copyToClipboard(newScimToken, 'SCIM token')}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}

                  {scimConfig?.token_hint && (
                    <p className="text-sm text-muted-foreground">
                      Current token ending in: <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">{scimConfig.token_hint}</code>
                    </p>
                  )}

                  <div className="flex items-center gap-3">
                    <Button
                      variant="outline"
                      className="gap-2"
                      onClick={handleGenerateScimToken}
                      disabled={generatingToken}
                    >
                      {generatingToken ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      Generate New Token
                    </Button>
                    {scimConfig?.token_hint && (
                      <Button
                        variant="destructive"
                        className="gap-2"
                        onClick={handleRevokeScimToken}
                      >
                        <Trash2 className="h-4 w-4" />
                        Revoke Token
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Sync Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Sync Status</CardTitle>
                  <CardDescription>
                    Overview of directory synchronization activity.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-6">
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold">{scimConfig?.synced_users_count ?? 0}</p>
                      <p className="text-sm text-muted-foreground mt-1">Synced Users</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-2xl font-bold">{scimConfig?.synced_groups_count ?? 0}</p>
                      <p className="text-sm text-muted-foreground mt-1">Synced Groups</p>
                    </div>
                    <div className="text-center p-4 rounded-lg bg-muted/50">
                      <p className="text-sm font-medium">{formatDateTime(scimConfig?.last_sync_at ?? null)}</p>
                      <p className="text-sm text-muted-foreground mt-1">Last Sync</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Provider Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add SSO Provider</DialogTitle>
            <DialogDescription>
              Configure a new identity provider for single sign-on.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="provider-type">Provider Type</Label>
              <Select
                value={form.type}
                onValueChange={(val: 'saml' | 'oidc') => setForm({ ...form, type: val })}
              >
                <SelectTrigger id="provider-type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="saml">SAML 2.0</SelectItem>
                  <SelectItem value="oidc">OpenID Connect (OIDC)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="provider-name">Display Name</Label>
              <Input
                id="provider-name"
                placeholder="e.g. Okta, Azure AD, Google Workspace"
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              />
            </div>

            <Separator />

            {form.type === 'saml' ? (
              <>
                <div className="space-y-2">
                  <Label htmlFor="entity-id">Entity ID</Label>
                  <Input
                    id="entity-id"
                    placeholder="https://idp.example.com/entity"
                    value={form.entity_id}
                    onChange={(e) => setForm({ ...form, entity_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="sso-url">SSO URL</Label>
                  <Input
                    id="sso-url"
                    placeholder="https://idp.example.com/sso/saml"
                    value={form.sso_url}
                    onChange={(e) => setForm({ ...form, sso_url: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="certificate">X.509 Certificate</Label>
                  <Textarea
                    id="certificate"
                    placeholder={"-----BEGIN CERTIFICATE-----\n...\n-----END CERTIFICATE-----"}
                    rows={6}
                    className="font-mono text-xs"
                    value={form.certificate}
                    onChange={(e) => setForm({ ...form, certificate: e.target.value })}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="client-id">Client ID</Label>
                  <Input
                    id="client-id"
                    placeholder="your-client-id"
                    value={form.client_id}
                    onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client-secret">Client Secret</Label>
                  <Input
                    id="client-secret"
                    type="password"
                    placeholder="your-client-secret"
                    value={form.client_secret}
                    onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discovery-url">Discovery URL</Label>
                  <Input
                    id="discovery-url"
                    placeholder="https://idp.example.com/.well-known/openid-configuration"
                    value={form.discovery_url}
                    onChange={(e) => setForm({ ...form, discovery_url: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <Label htmlFor="auto-discovery" className="cursor-pointer">Auto-discovery</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Automatically fetch provider configuration from the discovery URL.
                    </p>
                  </div>
                  <Switch
                    id="auto-discovery"
                    checked={form.auto_discovery}
                    onCheckedChange={(checked) => setForm({ ...form, auto_discovery: checked })}
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleCreateProvider} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete SSO Provider</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{providerToDelete?.display_name}</strong>?
              Users who authenticate through this provider will no longer be able to sign in.
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProvider} disabled={submitting} className="gap-2">
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete Provider
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default SSOAdminPage;
